import { SIGNUP_ROUTE } from '@/utils/constants'

/**
 * The `authRedirect` query param: where the auth flow sends the user once they
 * are signed in.
 *
 * Three places plant it (the consent guard, the incomplete-onboarding guard and
 * the consent-error handler) and one place replays it
 * (useAuthSummary.redirectAfterAuthSuccess). All of them have to agree on one
 * rule: an auth-flow path may never become the redirect target. Otherwise a
 * visitor who entered the flow at `/auth/login` is sent back to `/auth/login`
 * after signing up, sees the login screen of the account they just created and
 * only escapes when that view's own mounted hook gives up and replaces `/`.
 */

// `/auth`, its children, and `/onboarding`. Anchored, so it can never match a
// protocol-relative path.
const AUTH_FLOW_PATH_RE = /^\/(auth|onboarding)(\/|$)/

/**
 * @param {String} [path] - a route path
 * @returns {Boolean} whether it points into the auth flow
 */
export const isAuthFlowPath = path =>
  typeof path === 'string' && AUTH_FLOW_PATH_RE.test(path)

/**
 * The `authRedirect` part of a guard's redirect query. Empty when the path being
 * left is itself in the auth flow, so `{ ...to.query }` spreads add nothing.
 *
 * @param {String} [fullPath] - the route the user was heading for
 * @returns {Object} `{ authRedirect }`, or `{}` when there is nothing to return to
 */
export function authRedirectQuery(fullPath) {
  if (!fullPath || isAuthFlowPath(fullPath)) return {}
  return { authRedirect: fullPath }
}

/**
 * A route location into the auth flow (or any page a wall sends the viewer to)
 * that carries the page being left as `authRedirect`, next to the query the
 * destination already has (`GUEST_SIGNUP_ROUTE`).
 *
 * @param {String} to - destination path, optionally with a query string
 * @param {String} fromFullPath - the page being left, query included
 * @returns {Object} `{ path, query }` for router.push
 */
export function authFlowLocation(to, fromFullPath) {
  const { path, query } = splitPath(to)
  return { path, query: { ...query, ...authRedirectQuery(fromFullPath) } }
}

// `router.push({ path })` drops a query string inside `path`, so it goes in `query`.
function splitPath(fullPath) {
  const [path, search = ''] = fullPath.split('?')
  return { path, query: Object.fromEntries(new URLSearchParams(search)) }
}

/**
 * Where to send a user who just authenticated.
 *
 * @param {String} [authRedirect] - the route query value, which reaches us from
 *   the guards above but also straight from a hand-edited URL
 * @returns {String|null} the path to replay, or null to fall back to the app root
 */
export function postAuthRedirectPath(authRedirect) {
  if (typeof authRedirect !== 'string' || !authRedirect) return null
  if (isAuthFlowPath(authRedirect)) return null
  // only in-app destinations: an absolute or protocol-relative URL here would
  // turn the auth flow into an open redirect
  if (!authRedirect.startsWith('/') || authRedirect.startsWith('//')) return null
  return authRedirect
}

/**
 * The route location to replay after authentication: `postAuthRedirectPath`'s
 * destination with its own query, plus what else the auth screen's query held.
 *
 * @param {String} [authRedirect] - the route query value or the stashed path
 * @param {Object} currentQuery - the auth screen's route query
 * @returns {Object|null} `{ path, query }` for router.replace, or null to fall back to the app root
 */
export function postAuthRedirectLocation(authRedirect, currentQuery) {
  const destination = postAuthRedirectPath(authRedirect)
  if (!destination) return null

  const { authRedirect: _replayed, ...rest } = currentQuery
  const { path, query } = splitPath(destination)
  return { path, query: { ...rest, ...query } }
}

const STASH_KEY = 'authRedirect:stash'
const STASH_TTL = 48 * 60 * 60 * 1000 // recovery token lifetime (auth service)
// the verification mail may be read days later, and the link resent
export const VERIFICATION_STASH_TTL = 7 * 24 * 60 * 60 * 1000

/**
 * Persist the redirect target across flows that leave the app for email
 * (password recovery, the email verification), where the URL query cannot
 * carry it.
 *
 * @param {String} [authRedirect] - the route query value
 * @param {Number} [ttl] - lifetime in ms; the recovery token's by default
 */
export function stashAuthRedirect(authRedirect, ttl = STASH_TTL) {
  const path = postAuthRedirectPath(authRedirect)
  if (!path) return
  try {
    localStorage.setItem(STASH_KEY, JSON.stringify({ path, expires: Date.now() + ttl }))
  } catch {
    // storage unavailable — the flow degrades to losing the redirect, as before
  }
}

// Validated like the query param, so a hand-edited storage value gets the same
// open-redirect treatment.
function readStashedAuthRedirect(consume) {
  try {
    const raw = localStorage.getItem(STASH_KEY)
    if (!raw) return null
    if (consume) localStorage.removeItem(STASH_KEY)
    const { path, expires } = JSON.parse(raw)
    if (!expires || expires < Date.now()) return null
    return postAuthRedirectPath(path)
  } catch {
    return null
  }
}

/**
 * Consume the stashed redirect.
 *
 * @returns {String|null} the path to replay, at most once
 */
export const takeStashedAuthRedirect = () => readStashedAuthRedirect(true)

/**
 * Read the stashed redirect without consuming it (the resend puts it into the
 * mail link, the trip is not over).
 *
 * @returns {String|null} the path, or null
 */
export const peekStashedAuthRedirect = () => readStashedAuthRedirect(false)

// The screens a flow starts on ("Get rubra" sends a visitor to /onboarding);
// everything else under /auth is reached from inside the flow, query preserved.
const AUTH_ENTRY_PATH_RE = /^\/(auth(\/login|\/signup)?|onboarding)$/

/**
 * Every way into the auth flow brings the user back to the page it started on:
 * the router guard restarts a bare navigation into an entry screen with that
 * page as `authRedirect`, so no CTA has to carry it itself.
 *
 * @param {Object} to - target route
 * @param {Object} from - route being left
 * @returns {Object|null} the location to restart with, or null when the
 *   navigation already carries a page, starts the app (nothing matched yet),
 *   hops inside the flow, or is not an entry screen
 */
export function authEntryLocation(to, from) {
  if (!AUTH_ENTRY_PATH_RE.test(to.path) || to.query?.authRedirect) return null
  if (!from?.matched?.length || isAuthFlowPath(from.path)) return null
  const query = authRedirectQuery(from.fullPath)
  if (!query.authRedirect) return null
  return { path: to.path, query: { ...to.query, ...query } }
}

const ONBOARDING_PATH = '/onboarding'
// The auth screens the consent guard may interrupt (routes/auth.js requiresConsent).
const AUTH_SCREEN_RE = /^\/auth\/(login|signup)$/
// Query of /onboarding: the screen to resume once the terms are accepted.
export const AUTH_SCREEN_QUERY = 'authScreen'

/**
 * The onboarding location a page's visitor is sent to for the terms. An auth
 * screen travels along with its query (authRedirect, guest flags) so onboarding
 * resumes it — a visitor who clicked "Log in" lands on login, not on sign-up.
 * Any other page is the way back (`authRedirect`), as the guards plant it.
 *
 * @param {Object} to - the route being interrupted
 * @returns {Object} `{ path, query }` for router.push
 */
export function onboardingLocation(to) {
  const screen = AUTH_SCREEN_RE.test(to.path) ? { [AUTH_SCREEN_QUERY]: to.path } : {}
  return { path: ONBOARDING_PATH, query: { ...authRedirectQuery(to.fullPath), ...to.query, ...screen } }
}

/**
 * Where onboarding goes on once the terms are accepted: the auth screen it
 * interrupted, with the rest of its query. Null when none was ("Get rubra",
 * rubra.com, a hand-edited value): the caller picks the default screen.
 *
 * @param {Object} [query] - onboarding's route query
 * @returns {Object|null} `{ path, query }` for router.push
 */
export function onboardingResumeLocation(query = {}) {
  const { [AUTH_SCREEN_QUERY]: screen, ...rest } = query
  if (typeof screen !== 'string' || !AUTH_SCREEN_RE.test(screen)) return null
  return { path: screen, query: rest }
}

/**
 * Whether a navigation starts a new sign-up, which accepts the terms whatever
 * the device remembers: the stored consent may be another person's, or a
 * logged-out account's. Not a start: a guest upgrading their device account
 * (they accepted when it was made), onboarding sending its visitor on, a query
 * change on the screen itself, and a cold load (nothing matched yet: a reload,
 * a deep link, the extension's hand-off), where the stored consent still decides.
 *
 * @param {Object} to - target route
 * @param {Object} from - route being left
 * @param {String|null} userId - the session user, null when logged out
 * @returns {Boolean}
 */
export function startsSignup(to, from, userId) {
  if (to.path !== SIGNUP_ROUTE || userId) return false
  return !!from?.matched?.length && from.path !== ONBOARDING_PATH && from.path !== to.path
}
